<template>
	<v-row no-gutters class="pa-2 overflow-auto" style="height: 100%">
		<!-- Time tracking of the user in this page -->
		<UserTimeTracker page-key="ccmMedicalNote" />

		<v-col cols="12" class="pa-1" style="height: 100%">
			<v-card style="height: 100%; min-height: 300px">
				<v-card-title class="py-2 py-lg-4">
					<v-row no-gutters>
						<v-col class="text-left" :class="$vuetify.breakpoint.mdAndDown ? 'font-responsive-md' : ''">
							{{ $t('medical-notes-header') }}
						</v-col>
						<v-spacer />
						<v-col class="text-right">
							<ActionButton
								icon="mdi-file-delimited"
								tooltip="download-csv"
								:maxSize="25"
								:minSize="20"
								@clicked="downloadCSV()"
								:busy="downloading" />

							<v-icon
								color="primary"
								:size="$vuetify.breakpoint.mdAndDown ? 20 : 25"
								v-if="selectedCCMRecord?.id > 0"
								@click="synchronizeAddDrawers('ccmMedicalNoteAdd')">
								mdi-plus-circle
							</v-icon>
						</v-col>
					</v-row>
				</v-card-title>
				<v-card-text
					class="overflow-auto pa-3 pt-0"
					:style="$vuetify.breakpoint.lgAndUp ? 'height: calc(100% - 64px)' : 'height: calc(100% - 48px)'">
					<v-row no-gutters class="overflow-y-auto">
						<v-col
							lg="8"
							cols="12"
							class="pa-1"
							:class="$vuetify.breakpoint.lgAndUp ? 'pt-0' : ''"
							order-lg="1"
							order="2">
							<v-data-table
								:items="medicalNotes"
								:headers="headers"
								:items-per-page="-1"
								style="overflow-y: auto"
								:height="pageHeight(77)"
								fixed-header
								dense
								class="data-table-outline"
								:loading="loadingStatus"
								sort-by="noteDate"
								sort-desc
								:footer-props="{ 'items-per-page-text': '' }">
								<template v-slot:item="{ item, index }">
									<tr
										:class="getRowsForDataTable(index, selectedRowIndex === index)"
										@click="selectMedicalNote(item, index)">
										<td class="text-left px-1">
											<v-tooltip left v-if="item.ccmRecordId === 0 && item.additionalData">
												<template v-slot:activator="{ on }">
													<span v-on="on">
														{{ item.noteTitle }}
													</span>
												</template>
												{{ item.additionalData }}
											</v-tooltip>
											<span v-else>
												{{ item.noteTitle }}
											</span>
										</td>
										<td class="text-left px-1">
											{{ item.performedBy }}
										</td>
										<td class="text-left px-1">
											{{ formatTimestamp(item.noteDate) }}
										</td>
										<td class="text-left px-1">
											{{ formatDuration(item.timeSpentSeconds) }}
										</td>
										<td class="px-1 text-right">
											<span v-if="item.ccmRecordId === selectedCCMRecord.id">
												<ActionButton
													:color="selectedRowIndex === index ? 'white' : 'primary'"
													icon="mdi-delete"
													tooltip="delete"
													:maxSize="25"
													:minSize="20"
													@clicked="
														deleteId = item.id;
														showDeleteDialog = true;
													"
													:busy="deletingIds.includes(item.id)" />

												<v-tooltip left>
													<template v-slot:activator="{ on }">
														<v-icon
															:color="selectedRowIndex === index ? 'white' : 'primary'"
															:size="$vuetify.breakpoint.mdAndDown ? 20 : 25"
															v-on="on"
															@click.stop="selectMedicalNoteToEdit(item)">
															mdi-pencil
														</v-icon>
													</template>
													{{ $t('edit') }}
												</v-tooltip>
											</span>
										</td>
									</tr>
								</template>
								<template v-slot:[`footer.prepend`]>
									<v-row dense class="ma-0">
										<v-col class="text-left">
											<p class="ma-0">
												<v-tooltip top>
													<template v-slot:activator="{ on }">
														<v-icon class="ml-1" color="primary" size="25" v-on="on"> mdi-help-circle-outline </v-icon>
													</template>
													{{ $t('need-to-select-the-correct-record') }}
												</v-tooltip>
											</p>
										</v-col>
									</v-row>
								</template>
							</v-data-table>
						</v-col>
						<v-col lg="4" cols="12" class="pa-1 pt-0" order-lg="2" order="1">
							<v-card :height="pageHeight($vuetify.breakpoint.mdAndDown ? 36 : 136)">
								<v-card-title class="pa-2" :class="$vuetify.breakpoint.mdAndDown ? 'font-responsive-md' : ''">
									<div class="text-center" style="width: 100%">
										{{ $t('medical-note-details-header') }}
									</div>
								</v-card-title>
								<v-card-text
									class="overflow-auto d-flex flex-column"
									style="height: calc(100% - 102px)"
									ref="noteDetails">
									<!-- Note title -->
									<span class="pa-1" ref="noteTitle">
										<div class="text-b1 text-center">
											<b>{{ selectedMedicalNote?.noteTitle ?? '' }}</b>
										</div>
									</span>

									<!-- Note Type -->
									<span class="pa-1" ref="noteType">
										<div class="text-b1 text-center">
											{{ selectedMedicalNote?.medicalNoteType ?? '' }}
										</div>
									</span>

									<!-- Note content -->
									<v-row no-gutters class="d-flex flex-column flex-grow-1 pt-1">
										<v-col cols="12" class="pa-0">
											<v-textarea
												:value="selectedMedicalNote?.noteContent ?? ''"
												readonly
												hide-details
												no-resize
												outlined
												:label="$t('note-content')"
												:height="textAreaHeight" />
										</v-col>
									</v-row>
								</v-card-text>
								<v-card-actions v-if="selectedMedicalNote">
									<v-row no-gutters>
										<v-col cols="6">
											<p class="text-b1 text-left">
												<b>{{ $t('by') }}</b>
												: {{ selectedMedicalNote.performedBy }}
											</p>
										</v-col>
										<v-col cols="6">
											<p class="text-b1 text-right">
												<b>{{ $t('on') }}</b>
												:
												{{ selectedMedicalNote.noteDate ? formatTimestamp(selectedMedicalNote.noteDate) : '' }}
											</p>
										</v-col>
									</v-row>
								</v-card-actions>
							</v-card>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</v-col>

		<DeleteDialog
			:dialog.sync="showDeleteDialog"
			@confirm="
				showDeleteDialog = false;
				deleteMedicalNote(deleteId);
				deleteId = -1;
			" />
	</v-row>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import {
	getRowsForDataTable,
	formatDuration,
	formatTimestamp,
	downloadCSVWithKeys,
	getFullName,
} from '@/utils/utilities';
import DeleteDialog from '@/views/dialogs/DeleteDialog.vue';
import UserTimeTracker from '@/views/Client/components/UserTimeTracker.vue';
import ActionButton from '../ActionButton.vue';

export default {
	name: 'CCMMedicalNote',
	components: { UserTimeTracker, ActionButton, DeleteDialog },

	data () {
		return {
			loadingStatus: true,
			selectedMedicalNote: null,
			selectedRowIndex: -1,
			textAreaHeight: '200px',
			deleteId: -1,
			deletingIds: [],
			showDeleteDialog: false,
			downloading: false,
		};
	},

	computed: {
		...mapState({
			detailedUser: (state) => state.users.detailedUser,
			medicalNotes: (state) => state.ccmMedicalNote.ccmMedicalNoteByPatientId,
			selectedCCMRecord: (state) => state.ccmRecords.ccmRecord,
		}),

		...mapGetters({
			pageHeight: 'pageHeight',
		}),

		headers () {
			return [
				{
					text: this.$t('note-title-header'),
					sortable: false,
					value: 'noteTitle',
					class: 'px-1',
				},
				{
					text: this.$t('performed-by'),
					sortable: false,
					value: 'performedBy',
					class: 'px-1',
				},
				{
					text: this.$t('note-date'),
					sortable: false,
					value: 'noteDate',
					class: 'px-1',
				},
				{
					text: this.$t('time-spent-header'),
					sortable: false,
					value: 'timeSpentSeconds',
					class: 'px-1',
				},
				{
					text: this.$t('actions'),
					sortable: false,
					value: '',
					align: 'right',
					class: 'pl-1 pr-3',
				},
			];
		},
	},

	methods: {
		...mapActions({
			getMedicalNote: 'ccmMedicalNote/getCCMMedicalNoteByPatientId',
			getMedicalNoteById: 'ccmMedicalNote/getCCMMedicalNote',
			deleteMedicalNoteById: 'ccmMedicalNote/deleteCCMMedicalNote',
			storeCCMRecordsByPatientId: 'ccmRecords/storeMinimalCCMRecordsByPatientId',
		}),

		getRowsForDataTable,
		formatDuration,
		formatTimestamp,
		downloadCSVWithKeys,
		getFullName,

		synchronizeAddDrawers (drawer) {
			const parameters = {
				detailsDrawer: true,
				page: drawer,
			};
			this.$store.commit('SET_DETAILS_DRAWER', parameters);
		},
		synchronizeEditDrawers (drawer) {
			this.$store.commit('SET_PATIENT_DETAILS_DRAWER', drawer);
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', {
				updateDrawer: true,
				page2: 'ccmDetails',
			});
		},
		async selectMedicalNoteToEdit (item) {
			this.selectedMedicalNote = item;
			await this.$store.commit('ccmMedicalNote/STORE_CCM_MEDICAL_NOTE', item);
			this.getMedicalNoteById(item.id);
			this.synchronizeEditDrawers('ccmMedicalNoteEdit', item.id);
		},
		async deleteMedicalNote (id) {
			await this.deleteMedicalNoteById(id);
			await this.getMedicalNote(this.detailedUser.userData.id);
			await this.storeCCMRecordsByPatientId(this.detailedUser.userData.id);
			this.deletingIds = this.deletingIds.filter((item) => item !== id);
		},
		async selectMedicalNote (item, index) {
			this.selectedMedicalNote = item;
			this.selectedRowIndex = index;
		},
		calculateTextAreaHeight () {
			const noteDetailsHeight = this.$refs.noteDetails?.clientHeight || 0;
			const noteTypeHeight = this.$refs.noteType?.clientHeight || 0;
			const noteTitleHeight = this.$refs.noteTitle?.clientHeight || 0;
			const calculatedHeight = noteDetailsHeight - noteTypeHeight - noteTitleHeight;
			return calculatedHeight ? calculatedHeight - 20 + 'px' : '100px';
		},
		async downloadCSV () {
			this.downloading = true;
			downloadCSVWithKeys(this.medicalNotes, 'Medical_Notes_for_' + getFullName(this.detailedUser.userData), {
				ids: ['ccmRecordId'],
				data: ['noteTitle', 'noteContent', 'medicalNoteType', 'performedBy'],
				timestamps: ['noteDate'],
				timeSpent: ['timeSpentSeconds'],
			});
			this.downloading = false;
		},
	},

	async mounted () {
		if (!this.loadingStatus) {
			this.loadingStatus = false;
		}
		await this.getMedicalNote(this.detailedUser.userData.id);
		this.loadingStatus = false;
		this.textAreaHeight = this.calculateTextAreaHeight();
	},

	watch: {
		selectedMedicalNote: {
			handler () {
				this.$nextTick(() => {
					this.textAreaHeight = this.calculateTextAreaHeight();
				});
			},
			deep: true,
		},
	},
};
</script>
